import { Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { StripeElements } from '@stripe/stripe-js';
import { useStripe } from '@/providers/StripeProvider';
import type { ExpressCheckoutInputRef } from './ExpressCheckoutInput.types';

interface ExpressCheckoutInputProps {
    onConfirm: () => void;
    onError?: (error: unknown) => void;
}

export const ExpressCheckoutInput = ({
    onConfirm,
    onError,
    ref,
}: ExpressCheckoutInputProps & { ref?: Ref<ExpressCheckoutInputRef> }) => {
    const stripe = useStripe();
    const [stripeReady, setStripeReady] = useState(false);
    const elementsRef = useRef<StripeElements | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const isInitializedRef = useRef(false);
    const onConfirmRef = useRef(onConfirm);
    const onErrorRef = useRef(onError);

    // Keep refs updated
    onConfirmRef.current = onConfirm;
    onErrorRef.current = onError;

    useEffect(() => {
        // Only initialize once when stripe is available and container is mounted
        if (stripe && containerRef.current && !isInitializedRef.current) {
            isInitializedRef.current = true;

            // Stripe types don't include expressCheckout element, so we use type assertion
            const elements = stripe.elements({
                mode: 'setup',
                currency: 'usd',
                paymentMethodCreation: 'manual',
                paymentMethodTypes: ['card'],
                setupFutureUsage: 'off_session',
            } as Parameters<typeof stripe.elements>[0]);

            elementsRef.current = elements;

            // expressCheckout is not in Stripe types but is a valid element type
            const expressCheckout = (
                elements as unknown as { create: (type: string, options: object) => unknown }
            ).create('expressCheckout', { layout: 'auto' }) as {
                mount: (element: HTMLElement) => void;
                on: (event: string, handler: () => void) => void;
            };

            expressCheckout.mount(containerRef.current);

            // Match Vue behavior: show when ready event fires
            expressCheckout.on('ready', () => {
                setStripeReady(true);
            });

            expressCheckout.on('confirm', async () => {
                try {
                    await (elements as unknown as { submit: () => Promise<void> }).submit();
                    onConfirmRef.current();
                } catch (error) {
                    onErrorRef.current?.(error);
                }
            });
        }
    }, [stripe]);

    useImperativeHandle(ref, () => ({
        getElements: () => elementsRef.current,
    }));

    return <div ref={containerRef} className={stripeReady ? 'mb-4' : 'hidden'} />;
};

ExpressCheckoutInput.displayName = 'ExpressCheckoutInput';
